import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  Divider,
  Chip,
  Link,
  CircularProgress,
  Alert,
  Popover,
  Button
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  SmartToy as BotIcon
} from '@mui/icons-material';
import { ChatMessage, Citation } from '@nvgc/shared';

interface ChatInterfaceProps {
  onSendMessage: (message: string) => Promise<void>;
  messages: ChatMessage[];
  isLoading: boolean;
  error?: string;
  onRetry?: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  onSendMessage,
  messages,
  isLoading,
  error,
  onRetry
}) => {
  const [inputValue, setInputValue] = useState('');
  const [lastFailedMessage, setLastFailedMessage] = useState<string>('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getErrorMessage = (error: string): string => {
    // Provide user-friendly error messages
    if (error.toLowerCase().includes('network') || error.toLowerCase().includes('fetch')) {
      return 'Network error: Please check your internet connection and try again.';
    }
    if (error.toLowerCase().includes('unauthorized') || error.toLowerCase().includes('401')) {
      return 'Authentication error: Please log in again.';
    }
    if (error.toLowerCase().includes('rate limit') || error.toLowerCase().includes('429')) {
      return 'Rate limit exceeded: Please wait a moment before trying again.';
    }
    if (error.toLowerCase().includes('timeout')) {
      return 'Request timed out: The server is taking too long to respond. Please try again.';
    }
    if (error.toLowerCase().includes('server') || error.toLowerCase().includes('500')) {
      return 'Server error: Something went wrong on our end. Please try again later.';
    }
    return error; // Return original error if no specific case matches
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue.trim();
    setInputValue('');
    setLastFailedMessage(message); // Store in case we need to retry
    
    try {
      await onSendMessage(message);
      setLastFailedMessage(''); // Clear on success
    } catch (err) {
      // Error will be displayed via the error prop
      // Keep lastFailedMessage for retry
    }
  };

  const handleRetry = () => {
    if (lastFailedMessage && onRetry) {
      onRetry();
      // Optionally, you could also resend the last message directly
      // onSendMessage(lastFailedMessage);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Messages Area */}
      <Paper
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          mb: 2,
          maxHeight: 'calc(100vh - 200px)'
        }}
        variant="outlined"
      >
        {!isOnline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are currently offline. Messages cannot be sent until your connection is restored.
          </Alert>
        )}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              onRetry || lastFailedMessage ? (
                <Button color="inherit" size="small" onClick={handleRetry}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {getErrorMessage(error)}
          </Alert>
        )}
        
        <List sx={{ p: 0 }}>
          {messages.map((message, index) => (
            <React.Fragment key={message.id}>
              <MessageItem message={message} />
              {index < messages.length - 1 && <Divider sx={{ my: 2 }} />}
            </React.Fragment>
          ))}
          
          {isLoading && (
            <>
              <Divider sx={{ my: 2 }} />
              <ListItem sx={{ px: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BotIcon color="primary" />
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Thinking...
                  </Typography>
                </Box>
              </ListItem>
            </>
          )}
        </List>
        <div ref={messagesEndRef} />
      </Paper>

      {/* Input Area */}
      <Paper sx={{ p: 2 }} variant="outlined">
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask about Nevada gaming regulations..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || !isOnline}
              variant="outlined"
              size="small"
            />
            <IconButton
              type="submit"
              disabled={!inputValue.trim() || isLoading || !isOnline}
              color="primary"
              sx={{ alignSelf: 'flex-end' }}
              title={!isOnline ? "Cannot send while offline" : "Send message"}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

interface MessageItemProps {
  message: ChatMessage;
}

const MessageItem: React.FC<MessageItemProps> = ({ message }) => {
  const isUser = message.role === 'user';
  
  return (
    <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
      <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
        {/* Avatar */}
        <Box sx={{ mt: 0.5 }}>
          {isUser ? (
            <PersonIcon color="action" />
          ) : (
            <BotIcon color="primary" />
          )}
        </Box>

        {/* Message Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              mb: message.citations?.length ? 1 : 0
            }}
          >
            {message.content}
          </Typography>

          {/* Citations */}
          {message.citations && message.citations.length > 0 && (
            <CitationList citations={message.citations} />
          )}

          {/* Timestamp */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1 }}
          >
            {new Date(message.createdAt).toLocaleTimeString()}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );
};

interface CitationListProps {
  citations: Citation[];
}

const CitationList: React.FC<CitationListProps> = ({ citations }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setAnchorEl(event.currentTarget);
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setHoveredIndex(null);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Sources:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {citations.map((citation, index) => (
          <React.Fragment key={citation.chunkId}>
            <Chip
              label={`[${index + 1}] ${citation.documentTitle}${citation.pageNumber ? ` (p. ${citation.pageNumber})` : ''}`}
              size="small"
              variant="outlined"
              component={citation.sourceUrl ? Link : 'div'}
              href={citation.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              clickable={!!citation.sourceUrl}
              onMouseEnter={(e: any) => handleMouseEnter(e, index)}
              onMouseLeave={handleMouseLeave}
              sx={{
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  cursor: citation.sourceUrl ? 'pointer' : 'default'
                }
              }}
            />
            <Popover
              open={hoveredIndex === index}
              anchorEl={anchorEl}
              onClose={handleMouseLeave}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              disableRestoreFocus
              sx={{ pointerEvents: 'none' }}
            >
              <Box sx={{ p: 2, maxWidth: 400 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {citation.documentTitle}
                </Typography>
                {citation.pageNumber && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Page {citation.pageNumber}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Relevance: {Math.round((citation.relevanceScore || 0) * 100)}%
                </Typography>
                {citation.sourceUrl && (
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                    Click to view source
                  </Typography>
                )}
              </Box>
            </Popover>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};